import { useCallback, useMemo, type Dispatch, type ReactElement, type SetStateAction } from 'react';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import { SuitesTableV2 } from '../components/SuitesTableV2';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type {
  SuiteRunSortDirection,
  SuiteRunSortKey,
} from '@/features/suites/pages/suiteRunsTableDefinition';
import type {
  SuiteSortDirection,
  SuiteSortKey,
} from '@/features/suites/pages/suitesTableDefinition';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  ProjectDto,
  QueryResult,
  SuiteListItemDto,
  SuiteRunDto,
  SuiteRunStatus,
  TestRunDto,
} from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type TabbedSuitesPanelTab = 'suites' | 'executions';

type SuitesPageTabbedPanelProps = {
  panelActiveTab: TabbedSuitesPanelTab;
  setPanelActiveTab: Dispatch<SetStateAction<TabbedSuitesPanelTab>>;
  panelDateQuickFilter: DateQuickFilter;
  setPanelDateQuickFilter: Dispatch<SetStateAction<DateQuickFilter>>;
  panelFromDate: Dayjs | null;
  setPanelFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelToDate: Dayjs | null;
  setPanelToDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelSortKey: SuiteSortKey;
  setPanelSortKey: Dispatch<SetStateAction<SuiteSortKey>>;
  panelSortDirection: SuiteSortDirection;
  setPanelSortDirection: Dispatch<SetStateAction<SuiteSortDirection>>;
  panelFilterStatuses: SuiteRunStatus[];
  setPanelFilterStatuses: Dispatch<SetStateAction<SuiteRunStatus[]>>;
  panelFilterSuites: string[];
  setPanelFilterSuites: Dispatch<SetStateAction<string[]>>;
  panelFilterTags: string[];
  setPanelFilterTags: Dispatch<SetStateAction<string[]>>;
  panelExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelExecutionSortKey: SuiteRunSortKey;
  setPanelExecutionSortKey: Dispatch<SetStateAction<SuiteRunSortKey>>;
  panelExecutionSortDirection: SuiteRunSortDirection;
  setPanelExecutionSortDirection: Dispatch<SetStateAction<SuiteRunSortDirection>>;
  panelAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  panelDateWindow: {
    startDay: string | null;
    endDay: string | null;
    sparklineRangeDays: 7 | 14 | 30;
  };
  panelStatusFilterKey: string;
  panelSuiteFilterKey: string;
  panelTagFilterKey: string;
  panelNoRowsText: string;
  panelExecutionsNoRowsText: string;
  forceLoading?: boolean;
  debouncedPanelSearch: string;
  panelSuitesMeta: TableQueryMeta<SuiteListItemDto> | null;
  setPanelSuitesMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteListItemDto> | null>>;
  panelSuiteRunsMeta: TableQueryMeta<SuiteRunDto> | null;
  setPanelSuiteRunsMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteRunDto> | null>>;
  panelSuiteRunDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showPanelDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleDebouncedPanelSearchChange: (value: string) => void;
  panelQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  panelExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  panelNestedTestRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  tagOptionsOverride: TagOption[];
  actionsDisabled?: boolean;
  disableEntityLinks?: boolean;
  disableEntitySummaryLinks?: boolean;
  disableExecutionLinks?: boolean;
  disableExecutionSummaryLinks?: boolean;
  hideExecutionDetailsColumn?: boolean;
  hideExecutionPreviewDateLabel?: boolean;
  hideDateRangeLabel?: boolean;
  disablePageSizeSelect?: boolean;
  tableRowHeight?: number;
  requireAuth?: boolean;
  statusOptionsOverride?: Array<{ value: SuiteRunStatus; label: string }>;
  executionStatusOptionsOverride?: Array<{ value: SuiteRunStatus; label: string }>;
};

export const SuitesPageTabbedPanel = ({
  panelActiveTab: _panelActiveTab,
  setPanelActiveTab: _setPanelActiveTab,
  panelDateQuickFilter,
  setPanelDateQuickFilter: _setPanelDateQuickFilter,
  panelFromDate,
  setPanelFromDate: _setPanelFromDate,
  panelToDate,
  setPanelToDate: _setPanelToDate,
  panelColumnVisibilityModel: _panelColumnVisibilityModel,
  setPanelColumnVisibilityModel: _setPanelColumnVisibilityModel,
  panelSortKey,
  setPanelSortKey: _setPanelSortKey,
  panelSortDirection,
  setPanelSortDirection: _setPanelSortDirection,
  panelFilterStatuses: _panelFilterStatuses,
  setPanelFilterStatuses: _setPanelFilterStatuses,
  panelFilterSuites: _panelFilterSuites,
  setPanelFilterSuites: _setPanelFilterSuites,
  panelFilterTags: _panelFilterTags,
  setPanelFilterTags: _setPanelFilterTags,
  panelExecutionColumnVisibilityModel: _panelExecutionColumnVisibilityModel,
  setPanelExecutionColumnVisibilityModel: _setPanelExecutionColumnVisibilityModel,
  panelExecutionSortKey: _panelExecutionSortKey,
  setPanelExecutionSortKey: _setPanelExecutionSortKey,
  panelExecutionSortDirection: _panelExecutionSortDirection,
  setPanelExecutionSortDirection: _setPanelExecutionSortDirection,
  panelAvailableTags: _panelAvailableTags,
  suiteOptions: _suiteOptions,
  panelDateWindow,
  panelStatusFilterKey,
  panelSuiteFilterKey,
  panelTagFilterKey,
  panelNoRowsText,
  panelExecutionsNoRowsText: _panelExecutionsNoRowsText,
  forceLoading = false,
  debouncedPanelSearch,
  panelSuitesMeta: _panelSuitesMeta,
  setPanelSuitesMeta,
  panelSuiteRunsMeta: _panelSuiteRunsMeta,
  setPanelSuiteRunsMeta: _setPanelSuiteRunsMeta,
  panelSuiteRunDateBounds: _panelSuiteRunDateBounds,
  showPanelDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleDebouncedPanelSearchChange: _handleDebouncedPanelSearchChange,
  panelQueryFn,
  panelExecutionQueryFn,
  panelNestedTestRunsQueryFn,
  projectOptions = [],
  tagOptionsOverride,
  actionsDisabled = false,
  disableEntityLinks = false,
  disableEntitySummaryLinks: _disableEntitySummaryLinks = false,
  disableExecutionLinks: _disableExecutionLinks = false,
  disableExecutionSummaryLinks: _disableExecutionSummaryLinks = false,
  hideExecutionDetailsColumn: _hideExecutionDetailsColumn = false,
  hideExecutionPreviewDateLabel: _hideExecutionPreviewDateLabel = false,
  hideDateRangeLabel: _hideDateRangeLabel = true,
  disablePageSizeSelect: _disablePageSizeSelect = false,
  tableRowHeight: _tableRowHeight,
  requireAuth = true,
  statusOptionsOverride: _statusOptionsOverride,
  executionStatusOptionsOverride: _executionStatusOptionsOverride,
}: SuitesPageTabbedPanelProps): ReactElement => {
  const handleSuitesQueryMeta = useCallback(
    (meta: TableQueryMeta<SuiteListItemDto> | null) => {
      setPanelSuitesMeta(meta);
    },
    [setPanelSuitesMeta],
  );

  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> =>
      await panelQueryFn({
        ...input,
        sorts:
          input.sorts.length > 0
            ? input.sorts
            : [{ field: panelSortKey, direction: panelSortDirection }],
      }),
    [panelQueryFn, panelSortDirection, panelSortKey],
  );
  const tableExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteRunDto>> =>
      await panelExecutionQueryFn(input),
    [panelExecutionQueryFn],
  );
  const suitesQueryKey = useMemo<ReadonlyArray<string | number | null>>(
    () => [
      ...queryKeys.dataGrid(gridPaths.suites),
      panelDateQuickFilter,
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      showPanelDeltas ? 'deltas-on' : 'deltas-off',
      panelSortKey,
      panelSortDirection,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      panelDateWindow.startDay,
      panelDateWindow.endDay,
    ],
    [
      panelDateQuickFilter,
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      selectedOrganisationId,
      selectedProjectId,
      showPanelDeltas,
      panelSortDirection,
      panelSortKey,
    ],
  );

  return (
    <Stack gap="xl">
      <SuitesTableV2
        active
        actionsDisabled={actionsDisabled}
        forceLoading={forceLoading}
        linksDisabled={disableEntityLinks}
        queryKey={suitesQueryKey}
        projectOptions={projectOptions}
        queryFn={tableQueryFn}
        requireAuth={requireAuth}
        search={debouncedPanelSearch}
        selectedProjectId={selectedProjectId}
        noRowsText={panelNoRowsText}
        nestedSuiteRunsLatestOnly={
          panelDateQuickFilter === 'latest' && panelFromDate === null && panelToDate === null
        }
        suiteRunsScopeKey={JSON.stringify(suitesQueryKey)}
        suiteRunsQueryFn={tableExecutionQueryFn}
        tagOptionsOverride={tagOptionsOverride}
        testRunsQueryFn={panelNestedTestRunsQueryFn}
        onQueryMeta={handleSuitesQueryMeta}
      />
    </Stack>
  );
};
